/**
 * Rastreador de inatividade do usuário
 * 
 * Monitora eventos de interação (click, mouse, teclado, scroll)
 * e detecta quando o usuário está inativo há muito tempo.
 */

export interface InactivityTrackerConfig {
  /** Tempo de inatividade em segundos para considerar inativo (padrão: 1800 = 30 min) */
  inactivityTimeout?: number
  /** Callback quando o usuário fica inativo */
  onInactive?: () => void
  /** Callback quando o usuário volta a ficar ativo */
  onActive?: () => void
  /** Eventos para monitorar (padrão: click, mousemove, keydown, scroll, touchstart) */
  events?: string[]
}

const DEFAULT_INACTIVITY_TIMEOUT = 1800 // 30 minutos
const DEFAULT_EVENTS = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart']

export class InactivityTracker {
  private config: Required<InactivityTrackerConfig>
  private lastActivity: number = Date.now()
  private checkIntervalId: ReturnType<typeof setInterval> | null = null
  private wasInactive: boolean = false
  private isRunning: boolean = false
  private boundHandler: () => void

  constructor(config: InactivityTrackerConfig = {}) {
    this.config = {
      inactivityTimeout: config.inactivityTimeout ?? DEFAULT_INACTIVITY_TIMEOUT,
      onInactive: config.onInactive ?? (() => {}),
      onActive: config.onActive ?? (() => {}),
      events: config.events ?? DEFAULT_EVENTS,
    }
    
    this.boundHandler = this.handleActivity.bind(this)
  }

  /**
   * Registra atividade do usuário
   */
  private handleActivity() {
    const wasInactiveBefore = this.wasInactive
    this.lastActivity = Date.now()
    this.wasInactive = false
    
    if (wasInactiveBefore) {
      this.config.onActive()
    }
  }

  /**
   * Verifica se está inativo
   */
  private checkInactivity() {
    const inactiveFor = (Date.now() - this.lastActivity) / 1000
    
    if (inactiveFor >= this.config.inactivityTimeout && !this.wasInactive) {
      this.wasInactive = true
      this.config.onInactive()
    }
  }

  /**
   * Retorna se o usuário está atualmente inativo
   */
  isInactive(): boolean {
    const inactiveFor = (Date.now() - this.lastActivity) / 1000
    return inactiveFor >= this.config.inactivityTimeout
  }

  /**
   * Retorna há quanto tempo o usuário está inativo (em segundos)
   */
  getInactiveTime(): number {
    return (Date.now() - this.lastActivity) / 1000
  }

  /**
   * Retorna o timestamp da última atividade
   */
  getLastActivity(): number {
    return this.lastActivity
  }

  /**
   * Reseta o timer de inatividade
   */
  resetActivity() {
    this.handleActivity()
  }

  /**
   * Inicia o monitoramento
   */
  start() {
    if (this.isRunning) return
    
    this.isRunning = true
    this.lastActivity = Date.now()
    this.wasInactive = false

    // Adiciona listeners para eventos de atividade
    this.config.events.forEach(event => {
      document.addEventListener(event, this.boundHandler, { passive: true })
    })

    // Verifica inatividade a cada minuto
    this.checkIntervalId = setInterval(() => this.checkInactivity(), 60000)
  }

  /**
   * Para o monitoramento
   */
  stop() {
    if (!this.isRunning) return
    
    this.isRunning = false

    // Remove listeners
    this.config.events.forEach(event => {
      document.removeEventListener(event, this.boundHandler)
    })

    // Para o interval
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId)
      this.checkIntervalId = null
    }
  }

  /**
   * Atualiza a configuração
   */
  updateConfig(config: Partial<InactivityTrackerConfig>) {
    if (config.inactivityTimeout !== undefined) {
      this.config.inactivityTimeout = config.inactivityTimeout
    }
    if (config.onInactive !== undefined) {
      this.config.onInactive = config.onInactive
    }
    if (config.onActive !== undefined) {
      this.config.onActive = config.onActive
    }
  }
}

/**
 * Instância singleton para uso global
 */
let globalTracker: InactivityTracker | null = null

export function getInactivityTracker(config?: InactivityTrackerConfig): InactivityTracker {
  if (!globalTracker) {
    globalTracker = new InactivityTracker(config)
  } else if (config) {
    globalTracker.updateConfig(config)
  }
  return globalTracker
}

/**
 * Composable Vue para usar o tracker
 */
export function useInactivityTracker(config?: InactivityTrackerConfig) {
  const tracker = getInactivityTracker(config)
  
  return {
    tracker,
    isInactive: () => tracker.isInactive(),
    getInactiveTime: () => tracker.getInactiveTime(),
    resetActivity: () => tracker.resetActivity(),
    start: () => tracker.start(),
    stop: () => tracker.stop(),
  }
}
